import React from 'react';
import styled, { css } from 'styled-components';
import {
    Box,
    CaretUpOutlined,
    Collapse,
    Scrollable,
    color,
    palette,
    spacing,
    zIndex,
    fontSize,
    Button,
    Image as MaestroImage,
    Text as MaestroText,
    Group as MaestroGroup,
} from '@landr/maestro';
import {
    CreatorHeaderHeightPxMobile,
    CreatorHeaderHeightPxDesktop,
} from '../../constants';
import { CreatorTriggerProps } from './Creator';

const SampleHeight = 72;
const CreatorTableHeaderHeight = 40;
const CreatorTableGap = 20;
const CreatorFooterHeight = 112;

const CreatorWrapper = styled.div<{ isVisible: boolean }>`
    position: fixed;
    bottom: 0;
    width: 100%;
    z-index: ${zIndex('subOverlay')};
    padding-bottom: env(safe-area-inset-bottom, 0);
    background: ${color('background.base')};

    ${({ isVisible }) =>
        !isVisible &&
        css`
            display: none;
        `}
`;

const Creator = styled(Collapse)<{
    isOpen: boolean;
    isLoading: boolean;
    samplesCount: number;
}>`
    display: flex;
    flex-direction: column;
    padding-right: 0;
    padding-left: 0;
    border: none;
    user-select: none;

    > div:first-child {
        margin: 0 auto;
        padding: 0;
        height: ${() => CreatorHeaderHeightPxDesktop}px;

        ${({ theme }): string => theme.breakpoint('md')`
            height: ${CreatorHeaderHeightPxMobile}px;
        `}
    }

    > div:last-child {
        max-height: calc(80vh - ${spacing('mg')});
        overflow: hidden;

        ${({ isOpen, samplesCount, isLoading }) => {
            let calcHeight =
                samplesCount * SampleHeight +
                CreatorTableGap +
                CreatorTableHeaderHeight;

            if (samplesCount <= 7) {
                calcHeight += CreatorFooterHeight;
            }

            return (
                isOpen &&
                samplesCount !== 0 &&
                css`
                    height: ${calcHeight}px;
                `
            );
        }}

        > div {
            padding: 0;
        }

        &:before {
            content: '';
            position: relative;
            display: block;
            top: 1px;
            height: 1px;
            width: 100%;
            margin: 0 auto;
            background-color: ${palette('neutral.200')};
        }
    }
`;

const CreatorTrigger = styled(
    ({ isCreatorOpen, ...rest }: CreatorTriggerProps) => {
        const Icon = CaretUpOutlined;

        return <Icon {...rest} />;
    },
)<CreatorTriggerProps>`
    color: ${palette('neutral.900')};

    &:hover {
        color: ${palette('neutral.500')};
    }

    flex-shrink: 0;
    transform: ${({ isCreatorOpen }): string =>
        isCreatorOpen ? `rotate(-180deg)` : 'rotate(0)'};
    transition: transform 0.25s ease-out;
    cursor: pointer;

    margin-left: ${spacing('xl')};
`;

const CreatorHeaderContainer = styled.div`
    position: relative;
    padding: 0 ${spacing('lg')};
    margin: 0;
    font-size: 0;
    line-height: 1;

    ${({ theme }): string => theme.breakpoint('md')`
        padding-left: ${spacing('sm')};
        padding-right: ${spacing('sm')};
    `}
`;

const CreatorContentContainer = styled.div`
    margin: 0 auto;
    padding: 0;

    &:focus {
        outline: none;
    }

    ${({ theme }): string => theme.breakpoint('md')`
        padding-left: 0;
        padding-right: 0;
    `}
`;

const ContentScrollable = styled(Scrollable)`
    overflow-x: hidden;
    overflow-y: auto;
    max-height: calc(80vh - ${spacing('mg')});
    padding: 0;
    .simplebar-scrollbar:before {
        background: ${palette('neutral.1000')};
    }
`;

const ImagePlaceholder = styled(Box)<{
    samplesCount: number;
}>`
    height: ${({ samplesCount }): string =>
        samplesCount > 1
            ? `${
                  samplesCount * SampleHeight +
                  CreatorTableGap +
                  CreatorTableHeaderHeight
              }px`
            : '132px'};
`;

const Image = styled(MaestroImage)<{
    isSmall: boolean;
}>`
    height: ${({ isSmall }): string => (isSmall ? `45px` : '75px')};
`;

const Text = styled(MaestroText)`
    margin-top: ${spacing('md')};
`;

const Group = styled(MaestroGroup)``;

export const Styled = {
    Creator,
    CreatorTrigger,
    CreatorHeaderContainer,
    CreatorContentContainer,
    CreatorWrapper,
    ContentScrollable,
    ImagePlaceholder,
    Image,
    Text,
    Group,
};

export const PopoverHeader = styled(Box)`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
`;

export const PopoverClearButton = styled(Button)`
    color: ${palette('neutral.400')};
`;

export const PopoverBackButton = styled(Button)`
    color: ${palette('neutral.400')};
    float: left;
    font-size: ${fontSize('lg')};
    margin-top: ${spacing(5)};
    display: none;

    ${({ theme }): string => theme.breakpoint('md')`
        display: inherit;
    `}
`;
